import { useArtBlocksClient } from "@/client/components/common/ArtBlocksProvider";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/client/components/common/Command";
import {
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
} from "@/client/components/common/Popover";
import useIsMobile from "@/client/components/hooks/useIsMobile";
import { useNavbarHeight } from "@/client/components/hooks/useNavbarHeight";
import { cn } from "@/client/lib/classnames";
import {
  CollectionGridTokenDataFragment,
  ProjectGridDataFragment,
} from "@/gql/graphql";
import { graphql } from "@/gql/index";
import {
  CheckIcon,
  ChevronUpDownIcon,
  FunnelIcon,
} from "@heroicons/react/24/solid";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeatureSelector } from "./FeatureSelector";
import { TokenDetail } from "./TokenDetail";
import ReactTooltip from "react-tooltip";

export type FeatureValue = {
  feature: string;
  value: string | number | boolean;
};

const getProjectGridTokensQueryDocument = graphql(`
  query GetProjectGridTokens($projectId: String!) @cached(ttl: 300) {
    tokens_metadata(
      where: { project_id: { _eq: $projectId } }
      order_by: { invocation: asc }
    ) {
      ...CollectionGridTokenData
    }
  }

  fragment CollectionGridTokenData on tokens_metadata {
    id
    token_id
    features
    owner_address
    invocation
  }
`);

const OpenSeadragonViewer = dynamic(() => import("./OpenSeadragonViewer"), {
  ssr: false,
});

type CollectionGridPageProps = {
  projects: ProjectGridDataFragment[];
  initialCollectionIdOrName?: string;
};

export function CollectionGridPage({
  projects,
  initialCollectionIdOrName,
}: CollectionGridPageProps) {
  const openSeadragonContainerRef = useRef<HTMLDivElement>(null);
  const navbarHeight = useNavbarHeight();

  const {
    context: { graphqlClient },
  } = useArtBlocksClient();

  const [projectComboboxOpen, setProjectComboboxOpen] = useState(false);
  // The initialCollectionIdOrName comes from the query param, parsed on the server
  const [selectedProject, setSelectedProject] =
    useState<ProjectGridDataFragment | null>(() => {
      if (!initialCollectionIdOrName) {
        return projects[0];
      }

      const matchedProject = projects.find(
        (project) =>
          project.id === initialCollectionIdOrName ||
          project.name === initialCollectionIdOrName
      );
      return matchedProject ?? projects[0];
    });

  const gridSize =
    selectedProject?.slippy_map_grid_column_count ??
    Math.ceil(Math.sqrt(selectedProject?.invocations ?? 0));
  useEffect(() => {
    setSelectedTokenIndex(null);
    setHighlightedFeatures([]);
  }, [selectedProject?.id]);
  const handleProjectChange = useCallback(
    (project: ProjectGridDataFragment) => {
      setSelectedProject(project);
      setProjectComboboxOpen(false);

      // Update the URL without reloading the page. This is just to make it easier
      // to share the URL of a specific collection grid.
      const url = new URL(window.location.href);
      url.searchParams.set("collection", project.id);
      window.history.pushState({}, "", url.toString());
    },
    []
  );

  const { data: tokens, isLoading: tokenDataLoading } = useQuery({
    queryKey: ["project-tokens", selectedProject?.id],
    queryFn: async (): Promise<CollectionGridTokenDataFragment[]> => {
      if (!selectedProject) return [];

      const tokens = await graphqlClient.request(
        getProjectGridTokensQueryDocument,
        {
          projectId: selectedProject.id,
        }
      );
      return tokens.tokens_metadata;
    },
  });

  const [selectedTokenIndex, setSelectedTokenIndex] = useState<number | null>(
    null
  );
  const [mobileTokenPopoverExpanded, setMobileTokenPopoverExpanded] =
    useState(false);
  const tokenPopoverOpen = selectedTokenIndex !== null;
  const selectedToken =
    selectedTokenIndex !== null && tokens ? tokens[selectedTokenIndex] : null;

  // Token popover animation logic
  const { isMobile } = useIsMobile(); // This is safe to use here because this is a client only component
  const [hasEnteredInitially, setHasEnteredInitially] = useState(false);
  useEffect(() => {
    if (tokenPopoverOpen) {
      // Trigger the entrance animation after mount
      requestAnimationFrame(() => {
        setHasEnteredInitially(true);
      });
    } else {
      setHasEnteredInitially(false);
    }
  }, [tokenPopoverOpen]);

  const [isTokenPopoverClosing, setIsTokenPopoverClosing] = useState(false);
  const handleTokenPopoverClose = useCallback(() => {
    if (!isMobile || selectedTokenIndex === null) {
      setSelectedTokenIndex(null);
      return;
    }

    setIsTokenPopoverClosing(true);
    setTimeout(() => {
      setIsTokenPopoverClosing(false);
      setSelectedTokenIndex(null);
    }, 300);
  }, [isMobile, selectedTokenIndex]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [highlightedFeatures, setHighlightedFeatures] = useState<
    Array<FeatureValue>
  >([]);

  // Side effects to ensure only one popover is open at a time
  useEffect(() => {
    if (projectComboboxOpen) {
      setFilterOpen(false);
      handleTokenPopoverClose();
    }
  }, [projectComboboxOpen, handleTokenPopoverClose]);
  useEffect(() => {
    if (filterOpen) {
      setProjectComboboxOpen(false);
      handleTokenPopoverClose();
    }
  }, [filterOpen, handleTokenPopoverClose]);

  // When clicking a tile:
  // 1. If a utility popover is open (filter/project), close it without opening token details
  // 2. If token details are open, update the selected token
  // 3. Otherwise, open the token details
  const handleTileClick = useCallback(
    (tileIndex: number) => {
      if (!tokens) {
        return;
      }

      if (filterOpen) {
        setFilterOpen(false);
        return;
      }

      if (projectComboboxOpen) {
        setProjectComboboxOpen(false);
        return;
      }

      const token = tokens[tileIndex];
      if (!token) return;

      setSelectedTokenIndex(tileIndex);
    },
    [tokens, filterOpen, projectComboboxOpen]
  );

  const handleFeatureValueClick = (featureValue: FeatureValue) =>
    setHighlightedFeatures((prev) => {
      // If already highlighted, remove it
      const isHighlighted = prev.some(
        (f) =>
          f.feature === featureValue.feature && f.value === featureValue.value
      );

      if (isHighlighted) {
        return prev.filter(
          (f) =>
            !(
              f.feature === featureValue.feature &&
              f.value === featureValue.value
            )
        );
      }

      // If a feature with the same key but different value is already highlighted, remove it
      const isOtherValueHighlighted = prev.some(
        (f) =>
          f.feature === featureValue.feature && f.value !== featureValue.value
      );
      if (isOtherValueHighlighted) {
        return [
          ...prev.filter(
            (f) =>
              !(
                f.feature === featureValue.feature &&
                f.value !== featureValue.value
              )
          ),
          featureValue,
        ];
      }

      // Otherwise add it
      return [...prev, featureValue];
    });

  // We highlight features by putting a semi-transparent squar over
  // all of the tiles that don't match the highlighted features.
  const hiddenIndexes = useMemo(() => {
    if (!tokens || highlightedFeatures.length === 0) return [];

    // If a token matches all features, it should not be hidden
    return tokens.reduce<number[]>((matches, token, index) => {
      const matchesAllFeatures = highlightedFeatures.every(
        ({ feature, value }) =>
          token.features?.[feature]?.toString() === value.toString()
      );

      if (!matchesAllFeatures) {
        matches.push(index);
      }

      return matches;
    }, []);
  }, [tokens, highlightedFeatures]);
  const filteredTokensCount = (tokens?.length ?? 0) - hiddenIndexes.length;

  return (
    <div
      className="relative w-full"
      style={{ height: `calc(100dvh - ${navbarHeight}px)` }}
    >
      <div
        className={cn(
          "absolute z-10 transition-opacity opacity-80 md:top-5 md:left-5 md:right-auto hover:opacity-100 top-4 left-4 right-4",
          {
            "opacity-100":
              filterOpen || tokenPopoverOpen || projectComboboxOpen,
          }
        )}
      >
        <div className="relative flex gap-[2px]">
          <CollectionGridToolbarBox className="rounded-l-lg flex-1 md:flex-auto md:w-[300px]">
            <Popover
              open={projectComboboxOpen}
              onOpenChange={setProjectComboboxOpen}
            >
              <PopoverAnchor asChild>
                <div className="absolute inset-0 w-full pointer-events-none" />
              </PopoverAnchor>

              <PopoverTrigger asChild>
                <button
                  role="combobox"
                  aria-expanded={projectComboboxOpen}
                  className="flex items-center justify-between w-full p-3 truncate text-foreground bg-background"
                >
                  <span className="truncate">
                    {selectedProject?.name ?? "Select Project"}
                  </span>
                  <ChevronUpDownIcon className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                </button>
              </PopoverTrigger>
              <CollectionGridPopoverContent
                onInteractOutside={(e) => {
                  // Allow the tile click handler to close the project combobox
                  // so we don't open the token popover right away.
                  if (
                    openSeadragonContainerRef.current?.contains(
                      e.target as Node
                    )
                  ) {
                    e.preventDefault();
                    return;
                  }
                }}
              >
                <Command>
                  <CommandInput placeholder="Enter collection" />
                  <CommandList>
                    <CommandEmpty>No collection found.</CommandEmpty>
                    <CommandGroup>
                      {projects.map((project) => (
                        <CommandItem
                          key={project.name}
                          value={project.id}
                          onSelect={() => {
                            handleProjectChange(project);
                          }}
                          keywords={project.name ? [project.name] : undefined}
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedProject?.id === project.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {project.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </CollectionGridPopoverContent>
            </Popover>
          </CollectionGridToolbarBox>
          <CollectionGridToolbarBox>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverAnchor asChild>
                <div className="absolute inset-0 w-full pointer-events-none" />
              </PopoverAnchor>

              <PopoverTrigger asChild>
                <button
                  aria-expanded={filterOpen}
                  className="relative flex items-center p-3 bg-background"
                >
                  <FunnelIcon
                    className={cn(
                      "w-4 h-4 opacity-50 shrink-0 text-foreground-muted",
                      {
                        "opacity-10": tokenDataLoading,
                      }
                    )}
                  />
                  {highlightedFeatures.length ? (
                    <span className="absolute flex items-center justify-center w-3 h-3 text-xs font-medium text-foreground-muted/50 bottom-1 right-2">
                      {highlightedFeatures.length}
                    </span>
                  ) : null}
                </button>
              </PopoverTrigger>
              <CollectionGridPopoverContent
                onInteractOutside={(e) => {
                  // Allow the tile click handler to close the project combobox
                  // so we don't open the token popover right away.
                  if (
                    openSeadragonContainerRef.current?.contains(
                      e.target as Node
                    )
                  ) {
                    e.preventDefault();
                    return;
                  }
                }}
              >
                <div className="p-4">
                  <FeatureSelector
                    featureFields={selectedProject?.features?.feature_fields}
                    featreFieldsCounts={
                      selectedProject?.features?.feature_fields_counts
                    }
                    onFeatureChange={setHighlightedFeatures}
                    highlightedFeatures={highlightedFeatures}
                    filteredTokensCount={filteredTokensCount}
                    totalTokensCount={tokens?.length ?? 0}
                  />
                </div>
              </CollectionGridPopoverContent>
            </Popover>
          </CollectionGridToolbarBox>
          <CollectionGridToolbarBox className="p-3 rounded-r-lg">
            <ReactTooltip
              id="token-id"
              effect="solid"
              place="right"
              delayShow={500}
              disable={Boolean(selectedToken)}
              offset={{ right: 12 }}
            >
              <p>Click a token to view details</p>
            </ReactTooltip>
            <p
              data-tip
              data-for="token-id"
              className={cn(
                "tabular-nums font-sans cursor-default",
                selectedToken
                  ? "text-foreground-muted"
                  : "text-foreground-muted/50"
              )}
            >
              #{selectedToken?.invocation ?? "0000"}
            </p>
          </CollectionGridToolbarBox>
        </div>
        <Popover open={tokenPopoverOpen}>
          <PopoverAnchor asChild>
            <div className="fixed pointer-events-none bottom-4 left-4 right-4 md:static md:w-full md:bottom-auto md:left-auto md:right-auto" />
          </PopoverAnchor>
          <CollectionGridPopoverContent
            disableDefaultAnimations={isMobile}
            onEscapeKeyDown={() => {
              handleTokenPopoverClose();
            }}
            className={cn(
              "md:translate-y-0 max-md:transition-transform max-md:ease-[cubic-bezier(.455,.03,.515,.955)] duration-300",
              {
                "max-md:translate-y-[calc(100%-72px)]":
                  !mobileTokenPopoverExpanded && !isTokenPopoverClosing,
                "max-md:translate-y-0":
                  mobileTokenPopoverExpanded && !isTokenPopoverClosing,
                "max-md:translate-y-[calc(100%+24px)]":
                  isTokenPopoverClosing ||
                  (!hasEnteredInitially && !mobileTokenPopoverExpanded),
              }
            )}
          >
            {selectedToken && selectedProject ? (
              <TokenDetail
                highlightedFeatures={highlightedFeatures}
                onFeatureValueClick={handleFeatureValueClick}
                mobileTokenPopoverExpanded={mobileTokenPopoverExpanded}
                onToggleMobileTokenPopover={setMobileTokenPopoverExpanded}
                selectedProject={selectedProject}
                selectedToken={selectedToken}
                onClose={handleTokenPopoverClose}
              />
            ) : null}
          </CollectionGridPopoverContent>
        </Popover>
      </div>
      <div className="w-full h-full" ref={openSeadragonContainerRef}>
        <OpenSeadragonViewer
          selectedTokenIndex={selectedTokenIndex}
          hiddenTileIndexes={hiddenIndexes}
          gridSize={gridSize}
          onTileClick={handleTileClick}
          tokenAspectRatio={selectedProject?.aspect_ratio ?? 1}
          tileSource={`${process.env.NEXT_PUBLIC_SLIPPY_MAP_TILE_STORAGE_URL}/${selectedProject?.id}/out.dzi`}
        />
      </div>
    </div>
  );
}

function CollectionGridToolbarBox({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center border bg-background border-neutral-200 dark:border-neutral-700 overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
}

function CollectionGridPopoverContent({
  children,
  className,
  ...props
}: PopoverContentProps) {
  return (
    <PopoverContent
      className={cn(
        "bg-background w-[var(--radix-popover-trigger-width)] max-h-[calc(var(--radix-popover-content-available-height)_-_16px)] p-0 overflow-auto border-b border-black/10 dark:border-neutral-700",
        className
      )}
      {...props}
    >
      {children}
    </PopoverContent>
  );
}
